import { Box, Typography, useTheme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import React, { useLayoutEffect } from "react";

import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { useStickyState } from "../../../hooks/useStickyState";
import { useActiveLearner } from "../../../providers/ActiveLearnerProvider";
import { HEADER_WIDTH } from "../../nav/Header";
import AddLearnerButton from "./AddLearnerButton";

interface LearnerBannerButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  selected: boolean;
}

function LearnerBannerButton({
  children,
  onClick,
  selected
}: LearnerBannerButtonProps) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: "unset",
        height: 60,
        cursor: "pointer",
        backgroundColor: "white",
        borderRadius: 16,
        px: 28,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `2px solid transparent`,
        "&:hover": {
          border: `2px solid ${theme.palette.primary[800]}`
        },
        "&:focus-visible": {
          border: `2px solid ${theme.palette.primary[800]}`
        },
        transition: "border-color 0.1s ease-in-out",
        ...(selected && {
          border: `2px solid ${theme.palette.primary[400]}`
        }),
        [theme.breakpoints.down("md")]: {
          height: 48,
          px: 18
        }
      }}
    >
      {children}
    </Box>
  );
}

interface LearnerBannerProps {
  setLearnerBannerHeight: (height: number) => void;
}

export default function LearnerBanner({
  setLearnerBannerHeight
}: LearnerBannerProps) {
  const { currentUser } = useSession();
  const { headerHeight } = useStickyHeader();
  const { isSticky, sentinelRef, stickyRef } = useStickyState();
  const { activeLearner, setActiveLearner } = useActiveLearner();
  const { t } = useTranslation(
    "client\\components\\parentDashboardV2\\components\\LearnerBanner"
  );
  const theme = useTheme();
  const learners = currentUser?.children ?? [];

  useLayoutEffect(() => {
    if (stickyRef.current) {
      setLearnerBannerHeight(stickyRef.current.getBoundingClientRect().height);
    }
  }, [stickyRef, setLearnerBannerHeight]);

  return (
    <>
      <div ref={sentinelRef} style={{ height: "1px" }} />
      <Box
        ref={stickyRef}
        sx={{
          backgroundColor: "primary.50",
          position: "sticky",
          top: headerHeight,
          zIndex: 200,
          ...(isSticky && { boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)" }),
          transition: "box-shadow 0.2s ease-in-out"
        }}
      >
        <Box
          flex
          alignItems="center"
          sx={{
            height: 108,
            gap: 34,
            maxWidth: HEADER_WIDTH,
            mx: "auto",
            "@media (max-width: 1440px)": {
              px: 24
            },
            ...(!learners.length && {
              justifyContent: "center"
            }),
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "2.4rem",
              pointerEvents: "none",
              zIndex: 1
            },
            "&:before": {
              left: 0,
              background: `linear-gradient(to right, ${theme.palette.primary[50]}, transparent)`
            },
            "&:after": {
              right: 0,
              background: `linear-gradient(to left, ${theme.palette.primary[50]}, transparent)`
            },
            [theme.breakpoints.down("md")]: {
              "&:before, &:after": {
                width: "1.6rem"
              },
              "&:before": {
                background: `linear-gradient(to right, ${theme.palette.primary[50]}, transparent)`
              },
              "&:after": {
                background: `linear-gradient(to left, ${theme.palette.primary[50]}, transparent)`
              }
            },
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none"
            },
            [theme.breakpoints.down("md")]: {
              ...(learners.length > 0 && {
                height: 76
              }),
              px: 18,
              gap: 20
            }
          }}
        >
          {!learners.length && <AddLearnerButton />}
          {learners.length > 1 && (
            <LearnerBannerButton
              onClick={() => setActiveLearner(null)}
              selected={activeLearner === null}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  [theme.breakpoints.down("md")]: {
                    fontSize: "1.4rem"
                  }
                }}
              >
                {t("All Learners")}
              </Typography>
            </LearnerBannerButton>
          )}
          {learners.map(learner => (
            <LearnerBannerButton
              key={learner.uid}
              onClick={() => setActiveLearner(learner.uid)}
              selected={activeLearner?.uid === learner.uid}
            >
              <Box
                flex
                alignItems="center"
                justifyContent="center"
                sx={{
                  gap: 20
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    [theme.breakpoints.down("md")]: { display: "none" }
                  }}
                >
                  <AvatarImage
                    avatar={learner.avatar || DEFAULT_AVATAR}
                    width={40}
                    height={40}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    [theme.breakpoints.down("md")]: {
                      fontSize: "1.4rem"
                    }
                  }}
                >
                  {learner.name}
                </Typography>
              </Box>
            </LearnerBannerButton>
          ))}
        </Box>
      </Box>
    </>
  );
}
